import type { ReportForm } from './report-form'
import type { ReportTemplate } from '~/gql/reports/reports'
import { useQuery } from '@apollo/client/react'
import { ReportGranularity } from '@zeus/index'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_SESSIONS } from '~/gql/sessions/sessions'
import { tourAnchor } from '~/helpers/tour/anchors'
import { moveColumn, toggleColumn } from './report-form'
import ReportColumnPicker from './ReportColumnPicker'
import ReportFiltersFields from './ReportFiltersFields'
import { useReportTemplateForm } from './use-report-template-form'

type Props = {
  /** Le modèle à modifier ; nul pour en créer un. */
  template: ReportTemplate | null
  onSaved: () => void
  onDirtyChange: (dirty: boolean) => void
  /** Abandonne l'édition d'un modèle existant pour repartir d'un nouveau. */
  onReset: () => void
}

/** Composer un modèle de rapport (#163) : nom, granularité, colonnes, filtres. */
function ReportTemplateEditor({ template, onSaved, onDirtyChange, onReset }: Props) {
  const { t } = useTranslation()
  const editor = useReportTemplateForm(template, onSaved)
  const { form, errors } = editor
  const { data: sessionsData } = useQuery(GQL_SESSIONS)
  const { data: groupsData } = useQuery(GQL_GROUPS)
  const patch = (changes: Partial<ReportForm>) => editor.setForm(current => ({ ...current, ...changes }))

  useEffect(() => onDirtyChange(editor.dirty), [editor.dirty, onDirtyChange])

  return (
    <Card>
      <CardHeader>
        <CardTitle>{template ? t('reports.editor.editTitle', { name: template.name }) : t('reports.editor.newTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault()
            void editor.save()
          }}
        >
          <div {...tourAnchor('reports.editor')} className="flex flex-col gap-1.5">
            <Label htmlFor="reportName" required>{t('reports.editor.name')}</Label>
            <Input id="reportName" value={form.name} maxLength={120} onChange={e => patch({ name: e.target.value })} required />
          </div>
          <div {...tourAnchor('reports.granularity')} className="flex flex-col gap-1.5">
            <Label htmlFor="reportGranularity">{t('reports.editor.granularity')}</Label>
            <select
              id="reportGranularity"
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              value={form.granularity}
              onChange={e => void editor.changeGranularity(e.target.value as ReportGranularity)}
            >
              {Object.values(ReportGranularity).map(g => <option key={g} value={g}>{t(`reports.granularity.${g}`)}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">{t(`reports.granularityHelp.${form.granularity}`)}</p>
          </div>
          <ReportColumnPicker
            columns={editor.columns}
            selected={form.columns}
            onToggle={key => editor.setForm(current => toggleColumn(current, key))}
            onMove={(key, delta) => editor.setForm(current => moveColumn(current, key, delta))}
          />
          <ReportFiltersFields
            form={form}
            onChange={patch}
            sessions={sessionsData?.sessions ?? []}
            groups={groupsData?.groups ?? []}
            periodError={errors.period === true}
          />
          <div className="flex flex-wrap justify-end gap-2">
            {template && <Button type="button" variant="outline" onClick={onReset}>{t('reports.editor.newInstead')}</Button>}
            <Button {...tourAnchor('reports.save')} type="submit" disabled={Object.keys(errors).length > 0 || editor.saving}>
              {template ? t('common.save') : t('reports.editor.create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ReportTemplateEditor
